/**
 * ProviderDayScheduleModal
 * Simple modal for editing a single day's schedule (morning + afternoon, cada um com os seus
 * simultâneos). Since the API does bulk upsert (replace all), we merge the edited day with
 * all other days as they are on the server right now: a cópia que a página carregou pode estar
 * velha, e regravá-la desfaria o que outra pessoa alterou. Não envia `config`, então a duração
 * e os simultâneos padrão do prestador ficam como estão.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { WeekDay, WEEK_DAY_NAMES } from '../../../types/professionalSchedule';
import { ProviderWeeklySchedule } from '../../../types/provider';
import { providerService } from '../../../services/providerService';
import { mensagemDeFalha } from '../../../utils/acessoNegado';
import {
  classificarBlocosDoDia,
  capacidadeParaCampo,
  campoParaCapacidade,
  periodoPelaMetade,
} from './classificarBlocosDoDia';

interface ProviderDayScheduleModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  providerId: string | number;
  weekDay: WeekDay;
}

const timeFieldProps = { inputLabel: { shrink: true } };

const ProviderDayScheduleModal: React.FC<ProviderDayScheduleModalProps> = ({
  open,
  onClose,
  onSaved,
  providerId,
  weekDay,
}) => {
  const [morningStart, setMorningStart] = useState('');
  const [morningEnd, setMorningEnd] = useState('');
  const [morningCapacity, setMorningCapacity] = useState('');
  const [afternoonStart, setAfternoonStart] = useState('');
  const [afternoonEnd, setAfternoonEnd] = useState('');
  const [afternoonCapacity, setAfternoonCapacity] = useState('');
  // A grade lida do servidor ao abrir: é dela que saem os outros dias no salvar
  const [grade, setGrade] = useState<ProviderWeeklySchedule[]>([]);
  const [loading, setLoading] = useState(false);
  // Só vira verdadeiro quando a leitura deu certo: sem ela o salvar apagaria os outros dias
  const [carregou, setCarregou] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    let obsoleta = false;

    const carregar = async () => {
      setLoading(true);
      setCarregou(false);
      setError(null);
      setGrade([]);
      setMorningStart('');
      setMorningEnd('');
      setMorningCapacity('');
      setAfternoonStart('');
      setAfternoonEnd('');
      setAfternoonCapacity('');
      try {
        const { schedules } = await providerService.getWeeklyScheduleCompleto(providerId);
        if (obsoleta) return;

        const { manha, tarde } = classificarBlocosDoDia(schedules.filter(s => s.weekDay === weekDay));
        setGrade(schedules);
        setMorningStart(manha?.startTime || '');
        setMorningEnd(manha?.endTime || '');
        setMorningCapacity(capacidadeParaCampo(manha?.capacity));
        setAfternoonStart(tarde?.startTime || '');
        setAfternoonEnd(tarde?.endTime || '');
        setAfternoonCapacity(capacidadeParaCampo(tarde?.capacity));
        setCarregou(true);
      } catch (err) {
        if (!obsoleta) setError(mensagemDeFalha(err, 'Erro ao carregar horários existentes'));
      } finally {
        if (!obsoleta) setLoading(false);
      }
    };

    carregar();
    return () => {
      obsoleta = true;
    };
  }, [open, weekDay, providerId]);

  const handleSave = async () => {
    setError(null);

    const recusa = periodoPelaMetade(WEEK_DAY_NAMES[weekDay], {
      morningStart,
      morningEnd,
      afternoonStart,
      afternoonEnd,
    });
    if (recusa) {
      setError(recusa);
      return;
    }

    setSaving(true);
    try {
      // Build entries for the edited day
      const dayEntries: Omit<ProviderWeeklySchedule, 'id' | 'providerId' | 'isActive'>[] = [];
      if (morningStart && morningEnd) {
        dayEntries.push({
          weekDay,
          startTime: morningStart,
          endTime: morningEnd,
          capacity: campoParaCapacidade(morningCapacity),
        });
      }
      if (afternoonStart && afternoonEnd) {
        dayEntries.push({
          weekDay,
          startTime: afternoonStart,
          endTime: afternoonEnd,
          capacity: campoParaCapacidade(afternoonCapacity),
        });
      }

      // Keep entries from all other days unchanged
      const otherDayEntries = grade
        .filter(s => s.weekDay !== weekDay)
        .map(s => ({
          weekDay: s.weekDay,
          startTime: s.startTime,
          endTime: s.endTime,
          capacity: s.capacity || null,
        }));

      await providerService.saveWeeklySchedule(providerId, [...otherDayEntries, ...dayEntries]);
      onSaved();
    } catch (err) {
      setError(mensagemDeFalha(err, 'Erro ao salvar horários'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Horários — {WEEK_DAY_NAMES[weekDay]}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : carregou && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Manhã
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                label="Início"
                type="time"
                value={morningStart}
                onChange={e => setMorningStart(e.target.value)}
                size="small"
                fullWidth
                slotProps={timeFieldProps}
              />
              <TextField
                label="Fim"
                type="time"
                value={morningEnd}
                onChange={e => setMorningEnd(e.target.value)}
                size="small"
                fullWidth
                slotProps={timeFieldProps}
              />
              <TextField
                label="Simultâneos"
                type="number"
                value={morningCapacity}
                onChange={e => setMorningCapacity(e.target.value)}
                size="small"
                fullWidth
                placeholder="Padrão"
              />
            </Box>

            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Tarde
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Início"
                type="time"
                value={afternoonStart}
                onChange={e => setAfternoonStart(e.target.value)}
                size="small"
                fullWidth
                slotProps={timeFieldProps}
              />
              <TextField
                label="Fim"
                type="time"
                value={afternoonEnd}
                onChange={e => setAfternoonEnd(e.target.value)}
                size="small"
                fullWidth
                slotProps={timeFieldProps}
              />
              <TextField
                label="Simultâneos"
                type="number"
                value={afternoonCapacity}
                onChange={e => setAfternoonCapacity(e.target.value)}
                size="small"
                fullWidth
                placeholder="Padrão"
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          variant="contained"
          disabled={saving || loading || !carregou}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProviderDayScheduleModal;
